// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { ReactNode } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
  fontSize,
  spacing,
  mediaMax,
  borderRadius,
} from 'presentations/views/styles';
import Icon from 'presentations/views/components/atoms/Icon';
import EventRoleTag from 'presentations/views/components/molecules/EventRoleTag';
import { Status } from 'domains/models/event/status';
import { EventListItem } from 'presentations/api/schema/event';
import { DisplayType } from 'domains/models/event_type/displayType';
import { gray02, white } from 'presentations/views/styles/color';
import { VisibilityType } from 'domains/models/event/visibilityType';

interface Props {
  isHost: boolean | null;
  status: EventListItem['status'];
  displayStatus?: {
    displayType?: DisplayType;
    visibilityType?: VisibilityType;
  };
  flexDirection?: 'row' | 'column';
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  let visibilityLabel: ReactNode = null;
  if (props.displayStatus) {
    if (
      props.displayStatus.displayType === DisplayType.Private &&
      props.displayStatus.visibilityType === VisibilityType.Busy
    ) {
      visibilityLabel = (
        <MetaListItem noWrap>
          <DisplayTypeText isOutline={false}>
            {t('DisplayType_1')}
          </DisplayTypeText>
        </MetaListItem>
      );
    } else if (props.displayStatus.visibilityType === VisibilityType.Busy) {
      visibilityLabel = (
        <MetaListItem noWrap>
          <DisplayTypeText isOutline>{t('DisplayType_2')}</DisplayTypeText>
        </MetaListItem>
      );
    }
  }

  return (
    <MetaList flexDirection={props.flexDirection || 'row'}>
      {props.isHost !== null && (
        <MetaListItem noWrap>
          <EventRoleTag isHost={props.isHost} />
        </MetaListItem>
      )}
      {visibilityLabel}
      {props.status === Status.Cancel && (
        <MetaListItem>
          <MetaListItemIcon name="highlight_off" size={fontSize(20)} />
          <MetaListItemText>{t('Cancelled')}</MetaListItemText>
        </MetaListItem>
      )}
    </MetaList>
  );
}

const MetaList = styled.ul<{ flexDirection: string }>`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  ${({ flexDirection }) => css`
    flex-direction: ${flexDirection};
    align-items: ${flexDirection === 'row' ? 'center' : 'initial'};
  `}
  flex-direction: ${({ flexDirection }) => flexDirection};
`;
const MetaListItem = styled.li<{ noWrap?: boolean }>`
  margin-right: ${spacing(12)};
  display: flex;
  font-size: ${fontSize(13)};
  ${({ noWrap }) =>
    noWrap &&
    css`
      white-space: nowrap;
    `}

  ${mediaMax.tablet} {
    font-size: ${fontSize(10)};
    margin-right: ${spacing(8)};
  }
`;
const MetaListItemIcon = styled(Icon)`
  font-size: ${fontSize(20)};
  margin-right: ${spacing(4)};

  ${mediaMax.tablet} {
    font-size: ${fontSize(20)};
    margin-right: ${spacing(4)};
  }
`;
const MetaListItemText = styled.span``;

const DisplayTypeText = styled.div<{ isOutline: boolean }>`
  display: inline;
  padding: ${spacing(6)} ${spacing(20)};
  background-color: ${gray02};
  border-radius: ${borderRadius(4)};
  font-size: ${fontSize(11)};
  line-height: 100%;
  color: ${white};

  ${(props) =>
    props.isOutline &&
    css`
      background-color: ${white(props)};
      border: 1px solid ${gray02(props)};
      color: ${gray02(props)};
    `}

  ${mediaMax.tablet} {
    padding: ${spacing(4)} ${spacing(12)};
  }
`;
